import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ShieldCheckIcon,
  ClockIcon,
  ArrowPathIcon,
  PhotoIcon,
  CheckIcon,
  ArrowRightIcon,
  StarIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { useAuth } from '../contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { oauthAPI } from '../services/api';
import Navbar from './Navbar';
import Footer from './Footer';
import { captureAndStoreUTM } from '../utils/utmCapture';

const Home = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showStickyCTA, setShowStickyCTA] = useState(false);

  // Capture UTM parameters and wizard mode on page load
  useEffect(() => {
    captureAndStoreUTM();
    
    // Check for wizard=true param and store it for post-login redirect
    const wizardParam = searchParams.get('wizard');
    if (wizardParam === 'true') {
      localStorage.setItem('wizard_mode_pending', 'true');
    }
    
    // Store affiliate code if present in URL
    const affCode = searchParams.get('aff');
    if (affCode) {
      localStorage.setItem('vaulto_affiliate_code', affCode);
    }
  }, [searchParams]);

  // Show sticky CTA after scrolling past hero section
  useEffect(() => {
    const handleScroll = () => {
      // Show sticky CTA after scrolling 300px (earlier to capture more users)
      setShowStickyCTA(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch OAuth status if user is logged in
  const { data: oauthStatus } = useQuery({
    queryKey: ['oauth-status'],
    queryFn: () => oauthAPI.getStatus().then(res => res.data),
    enabled: isAuthenticated
  });

  // Smart CTA button logic
  const handleMainCTA = () => {
    if (!isAuthenticated) {
      // Not logged in -> go to registration
      // Preserve affiliate code from URL or localStorage
      const affCode = searchParams.get('aff') || localStorage.getItem('vaulto_affiliate_code');
      if (affCode) {
        navigate(`/register?aff=${affCode}`);
      } else {
        navigate('/register');
      }
    } else if (!oauthStatus?.instagram_connected || !oauthStatus?.google_connected) {
      // Logged in but missing connections -> go to settings
      navigate('/settings');
    } else {
      // Fully connected -> go to dashboard to start backup
      navigate('/dashboard');
    }
  };

  // Get dynamic button text
  const getButtonText = () => {
    if (!isAuthenticated) {
      return t('home.hero.cta.startFree');
    } else if (!oauthStatus?.instagram_connected || !oauthStatus?.google_connected) {
      return t('home.hero.cta.connectAccounts');
    } else {
      return t('home.hero.cta.goToDashboard');
    }
  };

  // Get dynamic subtitle text
  const getSubtitleText = () => {
    if (!isAuthenticated) {
      return t('home.hero.subtitleDynamic.notAuthenticated');
    } else if (!oauthStatus?.instagram_connected || !oauthStatus?.google_connected) {
      return t('home.hero.subtitleDynamic.notConnected');
    } else {
      return t('home.hero.subtitleDynamic.connected');
    }
  };

  const features = [
    {
      name: t('home.features.automaticBackup.name'),
      description: t('home.features.automaticBackup.description'),
      icon: ArrowPathIcon,
    },
    {
      name: t('home.features.secure.name'),
      description: t('home.features.secure.description'),
      icon: ShieldCheckIcon,
    },
    {
      name: t('home.features.realtime.name'),
      description: t('home.features.realtime.description'),
      icon: ClockIcon,
    },
    {
      name: t('home.features.fullResolution.name'),
      description: t('home.features.fullResolution.description'),
      icon: PhotoIcon,
    },
  ];

  const benefits = t('home.benefits.items', { returnObjects: true });

  return (
    <div className="bg-white overflow-x-hidden">
      {/* Navigation */}
      <Navbar variant="transparent" />

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 overflow-hidden min-h-screen">
        {/* Background decorative elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/50 to-pink-900/50"></div>
        <div className="absolute top-0 left-0 right-0 h-full">
          <div className="absolute top-10 left-10 w-32 h-32 sm:w-72 sm:h-72 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute top-32 right-10 w-48 h-48 sm:w-96 sm:h-96 bg-pink-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute bottom-10 left-1/3 w-32 h-32 sm:w-64 sm:h-64 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-500"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto">
          <div className="relative z-10 pb-8 sm:pb-16 md:pb-20 lg:pb-28 xl:pb-32 min-h-screen flex items-center">
            <main className="w-full mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-20 sm:pt-24">
              <div className="text-center lg:text-left lg:flex lg:items-center lg:justify-between">
                <div className="lg:w-1/2">
                  {/* Trust indicators */}
                  <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 mb-6">
                    <div className="flex items-center px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
                      <ShieldCheckIcon className="h-4 w-4 text-green-400 mr-2" />
                      <span className="text-xs text-white font-medium">Secure Instagram Graph API</span>
                    </div>
                    <div className="flex items-center px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
                      <StarIconSolid className="h-4 w-4 text-yellow-400 mr-2" />
                      <span className="text-xs text-white font-medium">4.9/5 Rating</span>
                    </div>
                    <div className="flex items-center px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
                      <LockClosedIcon className="h-4 w-4 text-blue-400 mr-2" />
                      <span className="text-xs text-white font-medium">Your Data, Your Drive</span>
                    </div>
                  </div>
                  
                  <h1 className="text-3xl tracking-tight font-extrabold text-white sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl leading-tight">
                    <span className="block">{t('home.hero.title').split(' ').slice(0, 2).join(' ')}</span>
                    <span className="block bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent leading-tight pb-2">
                      {t('home.hero.title').split(' ').slice(2).join(' ')}
                    </span>
                  </h1>
                  
                  <p className="mt-3 text-base text-gray-300 sm:mt-5 sm:text-lg lg:text-xl sm:max-w-xl sm:mx-auto lg:mx-0 md:mt-5 leading-relaxed">
                    {t('home.hero.subtitle')}
                  </p>
                  
                  <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row sm:justify-center lg:justify-start space-y-3 sm:space-y-0 sm:space-x-4">
                    <button
                      onClick={handleMainCTA}
                      className="group w-full sm:w-auto flex items-center justify-center px-6 py-3 sm:px-8 sm:py-4 border border-transparent text-sm sm:text-base font-semibold rounded-xl text-white bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                    >
                      {getButtonText()}
                      <ArrowRightIcon className="ml-2 h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform" />
                    </button>
                    <a
                      href="#features"
                      className="w-full sm:w-auto flex items-center justify-center px-6 py-3 sm:px-8 sm:py-4 border border-white/20 text-sm sm:text-base font-semibold rounded-xl text-white bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all duration-200"
                    >
                      {t('home.hero.cta.learnMore')}
                    </a>
                  </div>
                  
                  <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-center justify-center lg:justify-start space-y-2 sm:space-y-0 sm:space-x-6 text-xs sm:text-sm text-gray-300">
                    <div className="flex items-center">
                      <CheckIcon className="h-4 w-4 text-green-400 mr-2" />
                      <span>{t('home.hero.trust.freePlan')}</span>
                    </div>
                    <div className="flex items-center">
                      <CheckIcon className="h-4 w-4 text-green-400 mr-2" />
                      <span>{t('home.hero.trust.noCreditCard')}</span>
                    </div>
                    <div className="flex items-center">
                      <CheckIcon className="h-4 w-4 text-green-400 mr-2" />
                      <span>{t('home.hero.trust.setupMinutes')}</span>
                    </div>
                  </div>
                  
                  {/* Instagram Account Requirement Notice */}
                  <div className="mt-4 sm:mt-6 flex items-center justify-center lg:justify-start">
                    <div className="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
                      <PhotoIcon className="h-4 w-4 text-pink-400 mr-2 flex-shrink-0" />
                      <span className="text-xs sm:text-sm text-gray-200">
                        {t('home.hero.accountRequirement', { defaultValue: 'Works with Instagram Business & Creator accounts' })}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Visual/Illustration side */}
                <div className="mt-8 lg:mt-0 lg:w-1/2 lg:pl-8">
                  <div className="relative">
                    {/* Main illustration container */}
                    <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-lg rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 border border-white/20 shadow-2xl">
                      {/* Instagram to Google Drive flow */}
                      <div className="flex items-center justify-between mb-6 sm:mb-8 relative">
                        {/* Instagram side */}
                        <div className="text-center relative">
                          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-[#f09433] via-[#e6683c] via-[#dc2743] via-[#cc2366] to-[#bc1888] rounded-xl sm:rounded-2xl flex items-center justify-center mb-2 sm:mb-3 mx-auto shadow-lg relative overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent rounded-xl sm:rounded-2xl"></div>
                            <svg className="h-6 w-6 sm:h-8 sm:w-8 text-white relative z-10" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                            </svg>
                          </div>
                          <p className="text-white font-semibold text-xs sm:text-sm">{t('home.hero.illustration.instagram')}</p>
                          <p className="text-gray-300 text-xs hidden sm:block">{t('home.hero.illustration.yourContent')}</p>
                        </div>
                        
                        {/* Arrow */}
                        <div className="flex-1 px-2 sm:px-4">
                          <div className="relative">
                            <div className="h-0.5 bg-gradient-to-r from-pink-400 to-purple-400 w-full relative">
                              <div className="absolute inset-0 bg-gradient-to-r from-pink-400/40 to-purple-400/40 blur-sm"></div>
                            </div>
                            <ArrowRightIcon className="h-4 w-4 sm:h-6 sm:w-6 text-purple-400 absolute -top-2 sm:-top-3 -right-2 sm:-right-3 animate-pulse" />
                          </div>
                        </div>
                        
                        {/* Google Drive side */}
                        <div className="text-center relative">
                          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white rounded-xl sm:rounded-2xl flex items-center justify-center mb-2 sm:mb-3 mx-auto shadow-lg relative overflow-hidden">
                            <img src="/google_drive_icon_.png" alt="Google Drive" className="h-7 w-7 sm:h-9 sm:w-9 relative z-10" />
                          </div>
                          <p className="text-white font-semibold text-xs sm:text-sm">{t('home.hero.illustration.googleDrive')}</p>
                          <p className="text-gray-300 text-xs hidden sm:block">{t('home.hero.illustration.secureBackup')}</p>
                        </div>
                      </div>
                      
                      {/* Feature highlights */}
                      <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                        <div className="flex items-center text-white">
                          <ArrowPathIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-400 mr-2 sm:mr-3" />
                          <span className="text-xs sm:text-sm">{t('home.hero.illustration.feature1')}</span>
                        </div>
                        <div className="flex items-center text-white">
                          <ShieldCheckIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400 mr-2 sm:mr-3" />
                          <span className="text-xs sm:text-sm">{t('home.hero.illustration.feature2')}</span>
                        </div>
                        <div className="flex items-center text-white">
                          <ClockIcon className="h-4 w-4 sm:h-5 sm:w-5 text-purple-400 mr-2 sm:mr-3" />
                          <span className="text-xs sm:text-sm">{t('home.hero.illustration.feature3')}</span>
                        </div>
                      </div>
                      
                    </div>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-gradient-to-r from-gray-900 via-purple-900 to-gray-900 py-10 border-y border-purple-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">10,000+</p>
              <p className="text-sm text-gray-300 mt-1">Creators Protected</p>
            </div>
            <div>
              <p className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">1.5M+</p>
              <p className="text-sm text-gray-300 mt-1">Posts Backed Up</p>
            </div>
            <div>
              <p className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">99.9%</p>
              <p className="text-sm text-gray-300 mt-1">Uptime</p>
            </div>
            <div>
              <p className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">4.9/5</p>
              <p className="text-sm text-gray-300 mt-1">User Rating</p>
            </div>
          </div>
        </div>
      </div>

      {/* Social Proof Banner with CTA */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 py-6 sm:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
              <div className="flex -space-x-2">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-pink-400 to-purple-500 border-2 border-white flex items-center justify-center text-white text-xs font-bold">MK</div>
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-400 to-green-500 border-2 border-white flex items-center justify-center text-white text-xs font-bold">SJ</div>
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-400 to-pink-500 border-2 border-white flex items-center justify-center text-white text-xs font-bold">AL</div>
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-orange-400 to-red-500 border-2 border-white flex items-center justify-center text-white text-xs font-bold">+</div>
              </div>
              <div>
                <p className="text-white font-semibold text-sm sm:text-base">Join 10,247+ creators</p>
                <p className="text-gray-400 text-xs sm:text-sm">who already protect their Instagram content</p>
              </div>
            </div>
            <button
              onClick={handleMainCTA}
              className="group w-full sm:w-auto flex items-center justify-center px-6 py-3 border border-transparent text-sm font-semibold rounded-xl text-gray-900 bg-white hover:bg-gray-100 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
            >
              {t('home.hero.cta.startFree')}
              <ArrowRightIcon className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div id="features" className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-base text-primary-600 font-semibold tracking-wide uppercase">{t('navbar.features')}</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              {t('home.features.title')}
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
              {t('home.features.subtitle')}
            </p>
          </div>

          <div className="mt-10">
            <dl className="space-y-10 md:space-y-0 md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-10">
              {features.map((feature) => (
                <div key={feature.name} className="relative">
                  <dt>
                    <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-primary-500 text-white">
                      <feature.icon className="h-6 w-6" aria-hidden="true" />
                    </div>
                    <p className="ml-16 text-lg leading-6 font-medium text-gray-900">{feature.name}</p>
                  </dt>
                  <dd className="mt-2 ml-16 text-base text-gray-500">{feature.description}</dd>
                </div>
              ))}
            </dl>
          </div>

          {/* CTA after features */}
          <div className="mt-12 text-center">
            <button
              onClick={handleMainCTA}
              className="group inline-flex items-center justify-center px-8 py-4 border border-transparent text-base font-semibold rounded-xl text-white bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
            >
              {getButtonText()}
              <ArrowRightIcon className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <p className="mt-3 text-sm text-gray-500">
              {t('home.hero.trust.noCreditCard')} • {t('home.hero.trust.setupMinutes')}
            </p>
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-base text-primary-600 font-semibold tracking-wide uppercase">{t('home.testimonials.title')}</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              {t('home.testimonials.title')}
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 mx-auto">
              {t('home.testimonials.subtitle')}
            </p>
          </div>

          <div className="mt-12 grid gap-8 lg:grid-cols-3 lg:gap-x-8">
            {/* Testimonial 1 */}
            <div className="bg-white rounded-lg shadow-lg p-6 flex flex-col">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <StarIcon key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <blockquote className="text-gray-900 mb-4 flex-grow">
                "Vaulto saved my business! When Instagram suspended my account for 2 weeks over a false violation, 
                I had all my content safely backed up. I was able to continue my marketing campaigns and didn't lose a single post."
              </blockquote>
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-r from-pink-400 to-purple-500 flex items-center justify-center">
                    <span className="text-white font-semibold text-sm">SJ</span>
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">Sarah J.</p>
                  <p className="text-sm text-gray-500">Fashion Influencer • 250K followers</p>
                </div>
              </div>
              <button
                onClick={handleMainCTA}
                className="w-full mt-auto text-center text-sm font-semibold text-primary-600 hover:text-primary-700 py-2 border-t border-gray-100 group"
              >
                Protect your content too <ArrowRightIcon className="inline h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-white rounded-lg shadow-lg p-6 flex flex-col">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <StarIcon key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <blockquote className="text-gray-900 mb-4 flex-grow">
                "As a creative agency, we manage 50+ Instagram accounts. Vaulto gives us peace of mind knowing 
                all our clients' content is safely backed up. It's saved us countless hours and potential client relationships."
              </blockquote>
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-400 to-green-500 flex items-center justify-center">
                    <span className="text-white font-semibold text-sm">MD</span>
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">Mike D.</p>
                  <p className="text-sm text-gray-500">Creative Director • Digital Agency</p>
                </div>
              </div>
              <button
                onClick={handleMainCTA}
                className="w-full mt-auto text-center text-sm font-semibold text-primary-600 hover:text-primary-700 py-2 border-t border-gray-100 group"
              >
                Start backing up now <ArrowRightIcon className="inline h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-white rounded-lg shadow-lg p-6 flex flex-col">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <StarIcon key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <blockquote className="text-gray-900 mb-4 flex-grow">
                "I lost 3 years of content when my Instagram got hacked. Never again! Vaulto automatically backs up 
                everything to my Google Drive. It's like insurance for my digital life and business."
              </blockquote>
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-r from-purple-400 to-pink-500 flex items-center justify-center">
                    <span className="text-white font-semibold text-sm">AL</span>
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">Alex L.</p>
                  <p className="text-sm text-gray-500">Food Blogger • 180K followers</p>
                </div>
              </div>
              <button
                onClick={handleMainCTA}
                className="w-full mt-auto text-center text-sm font-semibold text-primary-600 hover:text-primary-700 py-2 border-t border-gray-100 group"
              >
                Don't wait until it's too late <ArrowRightIcon className="inline h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>

          {/* CTA after testimonials - high intent moment */}
          <div className="mt-12 text-center">
            <p className="text-lg text-gray-600 mb-4">Join creators who protect their content</p>
            <button
              onClick={handleMainCTA}
              className="group inline-flex items-center justify-center px-8 py-4 border border-transparent text-base font-semibold rounded-xl text-white bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
            >
              {getButtonText()}
              <ArrowRightIcon className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-4 text-sm text-gray-500">
              <span className="flex items-center">
                <CheckIcon className="h-4 w-4 text-green-500 mr-1" />
                {t('home.hero.trust.freePlan')}
              </span>
              <span className="flex items-center">
                <CheckIcon className="h-4 w-4 text-green-500 mr-1" />
                {t('home.hero.trust.noCreditCard')}
              </span>
            </div>
          </div>
          
        </div>
      </div>

      {/* Benefits Section */}
      <div className="bg-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              {t('home.benefits.title')}
            </h2>
            <p className="mt-4 max-w-3xl text-xl text-gray-500 lg:mx-auto">
              {t('home.benefits.subtitle')}
            </p>
          </div>

          {/* Warning Section */}
          <div className="mt-10 bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600 mt-1 flex-shrink-0" />
              <div className="ml-3">
                <h3 className="text-lg font-medium text-red-800">{t('home.benefits.warning.title')}</h3>
                <div className="mt-2 text-sm text-red-700">
                  <ul className="list-disc list-inside space-y-1">
                    {t('home.benefits.warning.items', { returnObjects: true }).map((item, index) => (
                      <li key={index} dangerouslySetInnerHTML={{ __html: item }} />
                    ))}
                  </ul>
                  <p className="mt-3 font-semibold">
                    {t('home.benefits.warning.footer')}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center">
                <CheckIcon className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                <span className="text-gray-700">{benefit}</span>
              </div>
            ))}
          </div>

          {/* Additional security benefits */}
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2">
            {t('home.benefits.protection', { returnObjects: true }).map((protection, index) => (
              <div key={index} className="flex items-center">
                <ShieldCheckIcon className="h-5 w-5 text-blue-500 mr-3 flex-shrink-0" />
                <span className="text-gray-700">{protection}</span>
              </div>
            ))}
          </div>

          {/* Urgent CTA after warning section */}
          <div className="mt-10 bg-gradient-to-r from-indigo-900 to-purple-900 rounded-2xl p-8 text-center">
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">
              Don't Wait Until It's Too Late
            </h3>
            <p className="text-gray-300 mb-6 max-w-xl mx-auto">
              Thousands of creators lose their content every day. Set up automatic backups in under 2 minutes.
            </p>
            <button
              onClick={handleMainCTA}
              className="group inline-flex items-center justify-center px-8 py-4 border border-transparent text-base font-semibold rounded-xl text-indigo-900 bg-white hover:bg-gray-100 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
            >
              {getButtonText()}
              <ArrowRightIcon className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <p className="mt-4 text-sm text-gray-400">
              {t('home.hero.trust.freePlan')} — {t('home.hero.trust.noCreditCard')}
            </p>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="py-8 sm:py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center lg:text-center">
            <h2 className="text-sm sm:text-base text-primary-600 font-semibold tracking-wide uppercase">{t('home.howItWorks.title')}</h2>
            <p className="mt-2 text-2xl sm:text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              {t('home.howItWorks.title')}
            </p>
          </div>
          
          <div className="mt-8 sm:mt-10">
            <div className="grid grid-cols-1 gap-6 sm:gap-8 md:grid-cols-3">
              <div className="text-center">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary-500 text-white mx-auto">
                  <span className="font-bold">1</span>
                </div>
                <h3 className="mt-4 text-base sm:text-lg font-medium text-gray-900">{t('home.howItWorks.step1.title')}</h3>
                <p className="mt-2 text-sm sm:text-base text-gray-500">
                  {t('home.howItWorks.step1.description')}
                </p>
              </div>
              
              <div className="text-center">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary-500 text-white mx-auto">
                  <span className="font-bold">2</span>
                </div>
                <h3 className="mt-4 text-base sm:text-lg font-medium text-gray-900">{t('home.howItWorks.step2.title')}</h3>
                <p className="mt-2 text-sm sm:text-base text-gray-500">
                  {t('home.howItWorks.step2.description')}
                </p>
              </div>
              
              <div className="text-center">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary-500 text-white mx-auto">
                  <span className="font-bold">3</span>
                </div>
                <h3 className="mt-4 text-base sm:text-lg font-medium text-gray-900">{t('home.howItWorks.step3.title')}</h3>
                <p className="mt-2 text-sm sm:text-base text-gray-500">
                  {t('home.howItWorks.step3.description')}
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-primary-700">
        <div className="max-w-2xl mx-auto text-center py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white sm:text-4xl">
            <span className="block">{t('home.cta.title')}</span>
            <span className="block">{t('home.cta.subtitle')}</span>
          </h2>
          <p className="mt-4 text-base sm:text-lg leading-6 text-primary-200">
            {t('home.cta.description')}
          </p>
          <button
            onClick={handleMainCTA}
            className="mt-6 sm:mt-8 w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 sm:px-5 border border-transparent text-sm sm:text-base font-medium rounded-md text-primary-600 bg-white hover:bg-primary-50 transition-colors"
          >
            {getButtonText()}
            <ArrowRightIcon className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
          </button>
          <p className="mt-3 text-xs sm:text-sm text-primary-200">
            {getSubtitleText()}
          </p>
        </div>
      </div>

      {/* Sticky CTA Bar - Mobile friendly */}
      <div 
        className={`fixed bottom-0 left-0 right-0 bg-gradient-to-r from-indigo-900 via-purple-900 to-pink-800 shadow-2xl z-50 transform transition-transform duration-300 ${
          showStickyCTA && !isAuthenticated ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-white text-center sm:text-left">
            <p className="font-semibold text-sm sm:text-base">🔒 Protect your Instagram content today</p>
            <p className="text-xs sm:text-sm text-gray-300 hidden sm:block">{t('home.hero.trust.freePlan')} • {t('home.hero.trust.noCreditCard')}</p>
          </div>
          <button
            onClick={handleMainCTA}
            className="group w-full sm:w-auto flex items-center justify-center px-6 py-2.5 sm:px-8 sm:py-3 border border-transparent text-sm sm:text-base font-semibold rounded-lg text-indigo-900 bg-white hover:bg-gray-100 shadow-lg transition-all duration-200"
          >
            {t('home.hero.cta.startFree')}
            <ArrowRightIcon className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Home; 